import React from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import {
  ButtonContainer,
  SelectorsWrapper,
  TranslationBoxWrapper,
  TranslationLabel,
} from './styles';
import ActionButton from '../../../../../components/ActionButton/ActionButton';
import { AnswerTypeEnum, QuestionTypeEnum } from '../../types';
import { TranslationKeys } from '../../../../../../i18n/locales/defaultResources';
import { useI18n } from '../../../../../../hooks/useI18n';
import { useTranslationContext } from '../../../../../../context/TranslationContext';

interface QuestionTypeSelectorProps {
  selectedQuestionType: QuestionTypeEnum | '';
  selectedAnswerType: AnswerTypeEnum | '';
  onQuestionTypeChange: (type: QuestionTypeEnum) => void;
  onAnswerTypeChange: (type: AnswerTypeEnum) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({
  selectedQuestionType,
  selectedAnswerType,
  onQuestionTypeChange,
  onAnswerTypeChange,
  onConfirm,
  onCancel,
}) => {
  const { t } = useI18n();
  const { isTranslationMode } = useTranslationContext();

  const TranslationBox = ({ children }: { children: React.ReactNode }) => (
    <TranslationBoxWrapper>{children}</TranslationBoxWrapper>
  );

  return (
    <SelectorsWrapper>
      {isTranslationMode ? (
        <>
          <TranslationLabel>
            {t(
              TranslationKeys.survey_question_type_selector_question_type_label
            )}
          </TranslationLabel>
          <TranslationBox>
            {t(
              TranslationKeys.survey_question_type_selector_question_type_standard
            )}
          </TranslationBox>
          <TranslationBox>
            {t(
              TranslationKeys.survey_question_type_selector_question_type_prefilled
            )}
          </TranslationBox>

          {selectedQuestionType === QuestionTypeEnum.STANDARD && (
            <>
              <TranslationLabel>
                {t(
                  TranslationKeys.survey_question_type_selector_answer_type_label
                )}
              </TranslationLabel>
              <TranslationBox>
                {t(
                  TranslationKeys.survey_question_type_selector_answer_type_single_choice
                )}
              </TranslationBox>
            </>
          )}
        </>
      ) : (
        <>
          <FormControl fullWidth>
            <InputLabel id="question-type-label">
              {t(
                TranslationKeys.survey_question_type_selector_question_type_label
              )}
            </InputLabel>
            <Select
              labelId="question-type-label"
              id="question-type-select"
              value={selectedQuestionType}
              label={t(
                TranslationKeys.survey_question_type_selector_question_type_label
              )}
              onChange={(e) =>
                onQuestionTypeChange(e.target.value as QuestionTypeEnum)
              }
            >
              <MenuItem value={QuestionTypeEnum.STANDARD}>
                {t(
                  TranslationKeys.survey_question_type_selector_question_type_standard
                )}
              </MenuItem>
              <MenuItem value={QuestionTypeEnum.PREFILLED}>
                {t(
                  TranslationKeys.survey_question_type_selector_question_type_prefilled
                )}
              </MenuItem>
            </Select>
          </FormControl>

          {selectedQuestionType === QuestionTypeEnum.STANDARD && (
            <FormControl fullWidth>
              <InputLabel id="answer-type-label">
                {t(
                  TranslationKeys.survey_question_type_selector_answer_type_label
                )}
              </InputLabel>
              <Select
                labelId="answer-type-label"
                id="answer-type-select"
                value={selectedAnswerType}
                label={t(
                  TranslationKeys.survey_question_type_selector_answer_type_label
                )}
                onChange={(e) =>
                  onAnswerTypeChange(e.target.value as AnswerTypeEnum)
                }
              >
                <MenuItem value={AnswerTypeEnum.YES_NO}>
                  {t(
                    TranslationKeys.survey_question_type_selector_answer_type_single_choice
                  )}
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </>
      )}

      <ButtonContainer>
        <ActionButton
          variant="ghost"
          buttonText={t(TranslationKeys.survey_question_type_selector_cancel)}
          onClickAction={onCancel}
        />
        <ActionButton
          variant="accept-blue"
          buttonText={t(TranslationKeys.survey_question_type_selector_add)}
          onClickAction={onConfirm}
          disabled={
            !isTranslationMode &&
            (!selectedQuestionType ||
              (selectedQuestionType === QuestionTypeEnum.STANDARD &&
                !selectedAnswerType))
          }
        />
      </ButtonContainer>
    </SelectorsWrapper>
  );
};

export default QuestionTypeSelector;
